/**
 * The macOS physical-pointer policy: whether agents may move the real
 * pointer to click or scroll. Rust owns the saved policy and reports it with
 * the two macOS grants it depends on (Accessibility and synthetic input), so
 * this module only reads and writes it — the macOS counterpart of
 * `input-consent.ts`, which answers the Windows question.
 */
import { invoke } from "@tauri-apps/api/core";
import { detectOS } from "@/lib/detect-os";
import { isTauri } from "@/lib/is-tauri";
import { getCurrentOwnerEpoch } from "@/lib/owner-state-client";
import { getWindowDisplayId } from "./use-window-role";

export type PointerControlPolicy =
  | "ask_every_task"
  | "always_allow"
  | "never_allow";

export type PointerControlStatus = {
  policy: PointerControlPolicy;
  accessibility: boolean;
  postEvent: boolean;
};

/** Pointer control is a macOS preference. */
export function pointerControlApplies(): boolean {
  return isTauri() && detectOS() === "macos";
}

export function getPointerControlStatus(): Promise<PointerControlStatus> {
  return invoke<PointerControlStatus>("pointer_control_policy_status");
}

export async function setPointerControlPolicy(
  policy: PointerControlPolicy,
): Promise<void> {
  await invoke("set_pointer_control_policy", {
    policy,
    displayId: getWindowDisplayId(),
    ownerEpoch: getCurrentOwnerEpoch(),
  });
}
